    <template>
    <div>
        <ui-modal
            v-if="isOpen"
            ref="editModal"
            size="xlarge"
            :title="(invoice.id ? 'Edit Invoice #' + invoice.id : 'Create Invoice')"
            @before-close="confirmOnClose"
            @close="isOpen = false; $emit('close')"
            dismiss-on="close-button esc"
        >
            <div v-if="!isLoading" class="container-fluid" style="padding: 0; margin-top: 1rem;">
                <ui-alert v-if="isActiveDeal" :dismissible="false" type="warning" style="margin-top: -1rem;">
                    <strong>Proceed with caution:</strong> This invoice relates to an active deal, any changes made may be overwritten. If you can, make your changes through the deal editor.
                </ui-alert>
                <ui-alert v-if="cancellationReason" :dismissible="false" type="info" >
                    <strong>Invoice {{ isRefunded ? 'refunded' : 'voided' }} due to {{ cancellationReason | lowerCase }}.</strong>
                    <p style="margin-bottom: 0;" v-if="invoice.cancellation_description">{{ invoice.cancellation_description }}</p>
                </ui-alert>
                <xero-sync-error-alert :owner="invoice" style="margin-top: -1rem;"></xero-sync-error-alert>
                <div class="row align-items-center">
                    <div class="col-2">
                        <ui-datepicker
                            v-model="taxAtProxy"
                            :custom-formatter="dateFormatter"
                            :disabled="!isEditable"
                            append-dropdown-to-body
                            :start-of-week="1"
                        >
                            Tax Date
                        </ui-datepicker>
                    </div>
                    <div class="col-2">
                        <team-picker 
                            v-model="teamProxy"
                            label="Team" 
                            :disabled="(!isEditable || team ? true : false)"
                        />
                    </div>
                    <div class="col-2" v-if="invoice.deal">
                        <div class="row align-items-center">
                            <div class="col">
                                <ui-textbox label="Deal #" v-model="invoice.deal.id" :disabled="true" />
                            </div>
                            <div class="col-5">
                                <ui-icon-button size="small" icon="link" @click="viewDeal" />
                            </div>
                        </div>
                    </div>
                    <div class="col offset-3 text-right">
                        <div style="font-size: .8rem; text-transform: uppercase;">{{ paymentStatusTitleText }}</div>
                        <div style="font-size: 2rem; line-height: normal">{{ paymentStatusText }}</div>
                        <ui-button 
                            v-if="balanceDue > 0 && numberOfPaymentsMade === 0 && !this.isVoid && !this.isRefunded" 
                            icon="attach_money" 
                            size="small" 
                            style="margin-top: .5rem"
                            @click="showPayments" 
                        >
                            Add Payment
                        </ui-button>
                        <div 
                            v-else-if="numberOfPaymentsMade > 0"
                        >
                            <a
                                href="#" 
                                @click.prevent="showPayments"
                            >
                                {{ numberOfPaymentsMade }} payments logged
                            </a>
                        </div>
                    </div>
                </div>

                <div class="row align-items-center">
                    <div class="col-6">
                        <contact-picker 
                            v-model="contactProxy" 
                            label="Customer" 
                            :new-contact-defaults="newContactDefaults"
                            :disabled="!isEditable"
                            show-account-code
                        />
                    </div>
                </div>

                <hr>

                <!-- Items !-->
                <div class="row" style="margin-top: 1.5rem;">
                    <div class="col-12">
                        <div class="row" style="margin-bottom: 1rem;">
                            <div class="col">
                                <h5>Items</h5>
                            </div>
                            <div class="col text-right">
                                <ui-button 
                                    size="small" 
                                    color="primary" 
                                    @click="addItem"
                                    :disabled="!isEditable"
                                >
                                    Add
                                </ui-button>
                            </div>
                        </div>
                    </div>
                    <div class="col-12" style="margin-bottom: -1rem;">
                        <div class="table-responsive">
                            <table class="table" style="margin-bottom: 0;">
                                <thead>
                                    <tr>
                                        <th style="width: 17%">Type</th>
                                        <th style="width: 50%;">Description</th>
                                        <th>Qty</th>
                                        <th>Unit</th>
                                        <th>VAT</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody v-if="invoice && invoice.items.length > 0">
                                    <tr 
                                        v-for="(item, index) in orderedInvoiceItems" 
                                        :key="index" 
                                        class="col-12"
                                    >
                                        <td>{{ item.type_text }}</td>
                                        <td class="truncate">
                                            <div>
                                                <div style="position: relative;"  v-if="item.instance && item.type === InvoiceItemTypes.TYPE_VEHICLE">
                                                    <span @click="viewStockListing(item.instance)" class="registration small" style="cursor: pointer;">{{ item.instance.vehicle.registration.formatted_vrm }}</span>
                                                    <span style="vertical-align: middle;">{{ item.instance.vehicle.file_as }}</span>
                                                </div>
                                                <div v-else>
                                                {{ item.description }}</div>
                                            </div>                                        
                                        </td>
                                        <td>{{ item.quantity }}</td>
                                        <td>{{ item.unit_cost | currency(2) }}</td>
                                        <td>{{ item.vat_rate }}</td>
                                        <td>{{ getInvoiceHelper().itemTotal(index) | currency(2) }}</td>
                                        <td class="text-right" style="padding-top: .5rem; padding-bottom: .5rem;">
                                            <ui-icon-button 
                                                v-if="getMenuOptions(item).length > 0" 
                                                has-dropdown 
                                                icon="more_vert" 
                                                :ref="'dropdownButton' + index"
                                                :disabled="!isEditable"
                                            >
                                                <ui-menu
                                                    @select="e => { costMenuSelected(e, item, index) }"
                                                    contain-focus
                                                    has-icons
                                                    slot="dropdown"
                                                    :options="getMenuOptions(item)"
                                                    @close="$refs['dropdownButton' + index][0].closeDropdown()"
                                                ></ui-menu>
                                            </ui-icon-button>
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody v-else>
                                    <tr>
                                        <td colspan="7">No items</td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="2" class="text-right"></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="row align-items-end">
                    <div class="col">

                <div v-if="shouldShowInvoiceNotes()" style="max-width: 100%;" class="truncate">
                    <div style="background: #fffcdc; padding: 1.5rem 1rem .5rem; margin-bottom: .5rem;">
                        <ui-textbox
                            ref="notes"
                            multi-line
                            label=""
                            placeholder="Notes"
                            :maxlength="1000"
                            enforce-maxlength
                            v-model="invoice.notes"
                            help="Visible on the customer invoice."
                        />
                    </div>
                </div>
                    </div>
                    <div class="col-4 offset-1">

                        <!-- Subtotal !-->
                        <div class="row align-items-center" style="margin-top: 1.5rem; font-size: 1.2rem;">
                            <div class="col"></div>
                            <div class="col text-right">
                                Subtotal
                            </div>
                            <div class="col text-right" style="max-width: 180px;">
                                {{ getInvoiceHelper().net() | currency(2) }}
                            </div>
                        </div>

                         <!-- VAT !-->
                        <div class="row align-items-center" style="font-size: 1.2rem; margin-top: .5rem;">
                            <div class="col text-right" style="font-size: .8rem;">
                                VAT @ {{ getInvoiceHelper().vatRate() }}% on {{ getInvoiceHelper().netVatable() | currency(2) }}
                            </div>
                            <div class="col text-right" style="max-width: 180px;">
                                {{ getInvoiceHelper().vat() | currency(2) }}
                            </div>
                        </div>

                        <!-- Total !-->
                        <div class="row align-items-center" style="font-size: 1.2rem; margin-top: .5rem;">
                            <div class="col text-right">
                                Total
                            </div>
                            <div class="col text-right" style="max-width: 180px;">
                                {{ getInvoiceHelper().total() | currency(2) }}
                            </div>
                        </div>

                        <!-- Balance Due !-->
                        <div class="row align-items-center" style="font-size: 1.5rem; margin-top: 1rem; font-weight: 600;">
                            <div class="col text-right" style=" border-top: 1px dashed #ccc; padding-top: 1rem">
                                Balance Due
                            </div>
                            <div class="col text-right" style="max-width: 180px; border-top: 1px dashed #ccc; padding-top: 1rem">
                                {{ balanceDue | currency(2) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!isLoading" slot="footer" style="width: 100%; text-align: right;">
                 <ui-button icon="more_horiz" has-dropdown ref="invoiceMenuButton" style="float: left;">
                    <ui-menu
                        contain-focus
                        has-icons
                        slot="dropdown"
                        :options="menuOptions"
                        @select="menuItemSelected"
                        @close="$refs.invoiceMenuButton.closeDropdown()"
                    ></ui-menu>
                    More
                </ui-button>
                <ui-button icon="print" @click="print" style="vertical-align: middle; margin-left: 10px; float: left;">Print</ui-button>
                <view-on-xero-button :owner="invoice" style="vertical-align: middle; float: left;"></view-on-xero-button>
                <ui-button :disabled="!canSave" @click="save(false)">Save</ui-button>
                <ui-button :disabled="!canSave" color="green" @click="save">Save &amp; Close</ui-button>
            </div>

            <div v-if="isLoading" class="container-fluid" style="padding: 4rem;">
                <ui-progress-circular style="margin:auto;" :size="48" :stroke="4"></ui-progress-circular>
            </div>
        </ui-modal>

        <invoice-item-modal 
            ref="itemModal" 
            :instance="instance" 
            :disable-instance-selection="invoice.deal ? true : false"
            @save="handleItemChanged" 
            @hide="suppressDirtyWarning = false; $refs['editModal'].open();"
        />

        <document-viewer 
            ref="documents" 
            :title="'Invoice ' + invoice.id" 
            :base-uri="documentUrl"
        />

        <invoice-payments-modal 
            ref="invoicePaymentsModal"
            :disabled="!canSave"
            @updated="paymentsUpdated"
            @cancel="cancel"
        />

        <invoice-cancellation-modal 
            ref="refundModal"
            @updated="invoiceCancelled"
        />

        <invoice-bulk-vehicle-modal
            ref="bulkVehicles"
            @processed="handleBulkVehicleAdd"
            :statuses="[5,10]"
        ></invoice-bulk-vehicle-modal>
    </div>
</template>

<script>
    import InvoicePaymentsModal from './InvoicePaymentsModal'
    import InvoiceCancellationModal from './InvoiceCancellationModal'
    import DocumentViewer from '../FullScreenDocumentViewer'
    import ContactPicker from './controls/KeenContactPicker'
    import TeamPicker from './controls/KeenTeamPicker'
    import InvoiceItemModal from './InvoiceItemModal'
    import { DealLifeCycleStatuses } from '../../classes/Api/Deal'
    import {
        InvoiceSchema,
        InvoiceResource,
        InvoiceHelper,
        InvoiceItemTypes,
        InvoiceStatuses,
        InvoiceCancellationReasons,
        InvoiceItemSchema
    } from '../../classes/Api/Invoice';
    import { ContactTypes, ContactTypeList } from '../../classes/Api/Contact';
    import Moment from 'moment'
    import Filters from '../../filters'
    import { savePaymentState } from '../../components/mixins/PaymentHelpers'
    import InvoiceBulkVehicleModal from "./BulkVehicleSelectModal.vue";
    import {goTo, shouldShowInvoiceNotes} from "../../utils";
    import {VatService} from "../../classes/VatService";
    import {latestXeroReference, latestXeroReferenceIsError, xeroErrorMessage} from "../../../ts/Accounts/Integrations";
    import XeroSyncErrorAlert from "../../../ts/Accounts/XeroSyncErrorAlert.vue";
    import ViewOnXeroButton from "../../../ts/Accounts/ViewOnXeroButton.vue";

    export default {
        filters: Filters,
        components: {
            ViewOnXeroButton,
            XeroSyncErrorAlert,
            InvoiceBulkVehicleModal,
            ContactPicker, 
            InvoiceItemModal, 
            TeamPicker,
            DocumentViewer,
            InvoicePaymentsModal,
            InvoiceCancellationModal,
        },
        props: {
            instance: {
                type: Object,
            },
            customButtonLabel: {
                type: String,
            },
            autoCloseOnSave: {
                type: Boolean,
                default: true,
            },
            team: {
                type: Object,
                required: false,
                default: null
            }
        },
        created() {
            this.reset()
        },
        data() {
            return {
                editingItemIndex: null,
                invoice: null,
                isLoading: false,
                isDirty: false,
                isPaymentsDirty: false,
                suppressDirtyWarning: false,
                InvoiceStatuses,
                InvoiceItemTypes,
                isOpen: false
            }
        },
        watch: {
            invoice: {
                handler: function(n, o) {
                    this.isDirty = true
                },
                deep: true
            }
        },
        computed: {
            orderedInvoiceItems() {
                return this.invoice.items.slice().filter(item => !item.is_deleted).sort((a,b) => {
                    // Settlements should always be at the bottom.
                    if(InvoiceItemTypes.TYPE_PART_EXCHANGE_SETTLEMENT === b.type) {
                        return -1
                    }

                    // Vehicles should always be at the top.
                    if(InvoiceItemTypes.TYPE_VEHICLE === b.type) {
                        return 1
                    }

                    return 0
                })
            },
            shouldWarnOnClose() {
                return !this.suppressDirtyWarning && this.isDirty
            },
            cancellationReason() {
                if (this.invoice && this.invoice.cancellation_reason) {
                    const reason = InvoiceCancellationReasons.find(
                        item => item.value === this.invoice.cancellation_reason
                    )

                    if (reason) {
                        return reason.sentance
                    }

                    return this.invoice.cancellation_reason
                }

                return null
            },
            menuOptions() {
                const options = [{
                    id: 'bulk.vehicles',
                    label: 'Add Vehicles',
                    icon: 'directions_car',
                    disabled: !this.canSave
                }]

                if (this.invoice.id > 0) {
                    options.push(
                        {
                            type: 'divider'
                        },
                        {
                            id: 'accounting.provider',
                            label: 'View on Sage',
                            icon: 'link',
                            disabled: !this.invoice.sync_record || !this.invoice.sync_record.related_url,
                        }, {
                            id: 'print',
                            label: 'Print',
                            icon: 'print',
                            disabled: !this.canPrint
                        }, {
                            id: 'email',
                            label: 'Email',
                            icon: 'email',
                            disabled: !this.canPrint
                        }, {
                            id: 'download',
                            label: 'Download PDF',
                            icon: 'download',
                            disabled: !this.canPrint
                        },
                        {
                            type: 'divider'
                        },
                        {
                            id: 'payment',
                            label: 'Payments',
                            icon: 'attach_money',
                            disabled: false
                        },
                        {
                            id: 'cancel',
                            label: 'Refund',
                            icon: 'undo',
                            disabled: !this.canRefund
                        },
                        {
                            id: 'cancel',
                            label: 'Void',
                            icon: 'clear',
                            disabled: !this.canVoid
                        },
                        {
                            type: 'divider'
                        },
                        {
                            id: 'delete',
                            label: 'Delete',
                            icon: 'delete',
                            disabled: !this.canDelete
                        })
                }

                return options
            },
            canRefund() {
                return this.isPaid && this.canSave
            },
            canVoid() {
                return !this.isPaid && this.canSave
            },
            canPrint() {
                return (this.invoice && null !== this.invoice.id)
            },
            canDelete() {
                return (this.invoice && null !== this.invoice.id)
            },
            canSave() {
                return (
                    this.invoice.status !== InvoiceStatuses.REFUNDED && 
                    this.invoice.status !== InvoiceStatuses.VOID
                )
            },
            isEditable() {
                return (!this.isVoid && !this.isRefunded)
            },
            isRefunded() {
                return (null !== this.invoice.refunded_at)
            },
            isVoid() {
                return (null !== this.invoice.voided_at)
            },
            isPaid() {
                return (0 === this.balanceDue && this.getInvoiceHelper().total() > 0)
            },
            paidPayments() {
                return this.invoice.payments.filter(item => item.is_posted)
            },
            numberOfPaymentsMade() {
                return this.invoice && this.invoice.payments ? this.invoice.payments.length : 0
            },
            balanceDue() {
                if (this.invoice) {
                    return this.getInvoiceHelper().balanceDue()
                }

                return 0
            },
            paymentStatusText() {
                if (this.isVoid) {
                    return 'VOID'
                } else if (this.isRefunded) {
                    return 'REFUNDED'
                } else if (this.isPaid) {
                    return 'PAID'
                } else {
                    return this.$options.filters.currency(this.balanceDue, 2)
                }
            },
            paymentStatusTitleText() {
                if (this.isPaid || this.isVoid || this.isRefunded) {
                    return 'Payment Status'
                } else {
                    return 'Balance Due'
                }
            },
            newContactDefaults() {
                return {
                    type: ContactTypes.CUSTOMER,
                    referral_source: window.marque.ui.otherReferralSource
                }
            },
            documentUrl() {
                return this.$root.buildUrl(
                    'api/invoices/%s/document', 
                    [this.invoice.id]
                );
            },
            refundedAtProxy: {
                get() {
                    if (typeof this.invoice.refunded_at === 'object') {
                        return this.invoice.refunded_at
                    }

                    return new Date()
                },
                set(value) {
                    this.invoice.refunded_at = value
                }
            },
            taxAtProxy: {
                get() {
                    if (typeof this.invoice.tax_at === 'object') {
                        return this.invoice.tax_at
                    }

                    return new Date()
                },
                set(value) {
                    this.invoice.tax_at = value
                }
            },
            isActiveDeal() {
                return this.invoice.deal && 
                    (
                        !this.invoice.deal.life_cycle_status || 
                        this.invoice.deal.life_cycle_status < DealLifeCycleStatuses.COMPLETED
                    )
            },
            isDeal() {
                return this.invoice.deal ? true : false
            },
            buttonLabel() {
                if (this.customButtonLabel) {
                    return this.customButtonLabel;
                }

                return 'Save';
            },
            contactProxy: {
                get() {
                    return this.invoice.contact;
                },
                set(value) {
                    this.$set(this.invoice, 'contact', value);
                    this.$set(this.invoice, 'contact_id', value.id);
                }
            },
            teamProxy: {
                get() {
                    return this.invoice.team ? this.invoice.team : {};
                },
                set(value) {
                    this.$set(this.invoice, 'team', value);
                    this.$set(this.invoice, 'team_id', value.id);
                }
            },
            canDestroy() {
                return (this.invoice.id && !this.invoice.deal) ? true : false
            }
        },
        methods: {
            shouldShowInvoiceNotes,
            viewStockListing(instance) {
                  goTo('/vehicles/' + instance.id + '/overview', true)
            },
            viewDeal() {
                if (this.confirmOnClose()) {
                    this.$root.navigateTo('deals/' + this.invoice.deal.id +'/edit')
                }
            },
            menuItemSelected(item) {
                switch (item.id) {
                    case 'print':
                        this.print()
                        break;
                    case 'email':
                        this.email()
                        break;
                    case 'download':
                        this.download()
                        break;
                    case 'delete':
                        this.destroy()
                        break;
                    case 'payment':
                        this.showPayments()
                        break;
                    case 'cancel':
                        this.cancel()
                        break;
                    case 'accounting.provider':
                        this.$root.goTo(this.invoice.sync_record.related_url);
                        break;
                    case 'bulk.vehicles':
                        this.$refs.bulkVehicles.open()
                        break
                }
            },
            getMarginStyles(margin) {
                return margin >= marque.ui.targetMargin ? 'color: green; font-weight: bold; ' : 'color: red; font-weight: bold; ';
            },
            showVehicle(instance) {
                if (!this.isDirty || this.isDirty && this.confirmOnClose({ cancel: () => { }})) {
                    this.$root.navigateTo('vehicles/%s/overview', [instance.id])
                }
            },
            invoiceCancelled(invoice) {
                this.$set(this, 'invoice', invoice)
                this.$emit('saved', invoice)
                this.$nextTick(() => {
                    this.isDirty = false
                    this.isPaymentsDirty = false
                })
            },
            paymentsUpdated(payments) { 
                this.$set(this.invoice, 'payments', payments); 
                this.isPaymentsDirty = true 
            },
            showPayments() {
                this.$refs.invoicePaymentsModal.open(this.invoice)
            },
            canDeleteItem(item) {
                // Prevent deals PXs from being deleted.
                return !(this.isActiveDeal || InvoiceItemTypes.TYPE_PART_EXCHANGE_SETTLEMENT === item.type)
            },
            print() {
                if (this.confirmOnClose()) {
                    this.$refs.documents.open();
                }
            },
            email() {
                if (this.confirmOnClose()) {
                    this.$refs.documents.open('email', {
                        defaultMessageContact: this.contactProxy
                    });
                }
            },
            download() {
                if (this.confirmOnClose()) {
                    this.$refs.documents.open('download');
                }
            },
            reset() {
                this.invoice = new InvoiceSchema({
                    tax_at: new Date(),
                    is_posted: true, 
                    status_text: 'Active',
                    payments: [],
                    contact: {},
                    team: this.team,
                    team_id: this.team ? this.team.id : null
                })

                this.isLoading = false
                this.isDirty = false
                this.isPaymentsDirty = false
                this.suppressDirtyWarning = false
                this.isOpen = false
            },
            getInvoiceHelper() {
                return new InvoiceHelper({ ...this.invoice, ...{ items: this.orderedInvoiceItems } });
            },
            open(invoice = null) {
                this.reset()

                this.isOpen = true

                this.$nextTick(() => {
                    if (invoice) {
                        if (invoice.id > 0) {
                            let relations = [
                                'deal',
                                'contact',
                                'items',
                                'items.instance',
                                'location',
                                'xeroReferences',
                            ]

                            this.isLoading = true;

                            InvoiceResource.find(invoice.id, { with: relations }).then(response => {
                                this.setInvoice(response.data)

                                this.isLoading = false;
                            });
                        } else {
                            this.setInvoice(invoice)
                        }
                    }

                    this.$refs['editModal'].open();
                })
            },
            setInvoice(invoice) {
                if (typeof invoice.tax_at === 'string') {
                    invoice.tax_at = (new Moment(invoice.tax_at)).toDate();
                }

                if (typeof invoice.refunded_at === 'string') {
                    invoice.refunded_at = (new Moment(invoice.refunded_at)).toDate();
                }

                this.invoice = _.cloneDeep(invoice);

                for (let i in this.invoice.items) {
                    this.$set(this.invoice.items[i], 'is_deleted', false)
                }

                this.$nextTick(() => {
                    this.isDirty = false
                })
            },
            confirmOnClose(ref = {}) {
                if (
                    // Warn on closing with unsaved changes
                    (this.shouldWarnOnClose && 
                    !confirm('Are you sure that you want continue without saving you changes?'))
                ) {
                    if (ref.cancel) cancel()

                    return false;
                }

                return true;
            },
            close() {
                this.$refs['editModal'].close();
            },
            destroy() {
                if (confirm('Are you sure you want to delete this invoice?')) {
                    this.isLoading = true;
                    InvoiceResource.destroy(this.invoice.id).then(response => {
                        this.$emit('destroyed', response.data);
                        this.isLoading = false;

                        this.$refs['editModal'].close();
                    }, (e) => { handleApiError(e); this.isLoading = false; });
                }
            },
            cancel() {
                if (this.shouldWarnOnClose) {
                    alert('Please save your changes before continuing');
                    return
                }

                this.$refs.refundModal.open(this.invoice)
            },
            onSave(callback) {
                if (callback) {
                    this.$once('saved', callback)
                }
            },
            save(allowAutoClose = true) {
                // Warn on saving when invoice is paid and balance is not zero
                if (this.balanceDue !== 0 && this.invoice.status === InvoiceStatuses.PAID 
                    && !confirm('The amount due will no longer match the amount paid, are you sure you want to save?')) {
                    return;
                }

                this.isLoading = true;

                let payments = _.cloneDeep(this.invoice.payments);

                let invoice = Object.assign({}, this.invoice, {
                    contact: null,
                    team: null,
                    payments: null,
                });

                invoice.items = invoice.items.map(item => {
                    return Object.assign(item, {
                        related_instance: null,
                    });
                });

                InvoiceResource.storeOrUpdate(invoice.id, invoice).then(response => {

                    // Set the invoice ID if it's not set.
                    if (!invoice.id) {
                        invoice.id  = response.data.id
                    }

                    savePaymentState(payments, response.data.id).then((payments) => {

                        let params = { with: ['items.instance', 'contact', 'team', 'payments', 'deal'] }

                        InvoiceResource.find(invoice.id, params).then(response => {
                            this.invoice = response.data;
                            this.$emit('saved', this.invoice);

                            this.$nextTick(() => {
                                this.isLoading = false;
                                this.isDirty = false;
                                this.isPaymentsDirty = false
                                
                                if (allowAutoClose && this.autoCloseOnSave) {
                                    this.$refs['editModal'].close();
                                }
                            })  
                        })

                    }, (e) => { handleApiError(e); this.isLoading = false })

                }, (e) => { handleApiError(e); this.isLoading = false; });
            },
            dateFormatter(date) {
                return (new Moment(date)).format('DD/MM/YYYY')
            },
            addItem() {
                this.editingItemIndex = null

                this.suppressDirtyWarning = true;

                this.$refs['itemModal'].open()
            },
            handleBulkVehicleAdd({instance, line}) {
                let vat_rate = instance.is_vat_qualifier ? 'S' : 'N'
                let unit_cost = 0

                if (line[2]) {
                    vat_rate = line[2].toUpperCase().replace('VQ', 'S')
                }

                const vehicleRate = instance.is_vat_qualifier ? 'S' : 'N'

                if (vat_rate !== vehicleRate) {
                    alert(line[0] + ': VAT changed as this is a ' + (instance.is_vat_qualifier ? 'VAT Qualifier' : 'Margin Car'))
                    vat_rate = vehicleRate
                }

                if (line[1]) {
                    unit_cost = parseInt(line[1])

                    if (instance.is_vat_qualifier) {
                        unit_cost = (new VatService()).unvatify(unit_cost)
                    }
                }

                this.handleItemAdd(new InvoiceItemSchema({
                    instance_id: instance.id,
                    instance: instance,
                    description: instance.search_description,
                    unit_cost,
                    type: InvoiceItemTypes.TYPE_VEHICLE,
                    type_text: 'Vehicle',
                    vat_rate
                }))
            },
            handleItemAdd(item) {
                if (item.type === InvoiceItemTypes.TYPE_VEHICLE) {
                   const existing = this.invoice.items.find(i => {
                       return (
                           i.type === InvoiceItemTypes.TYPE_VEHICLE &&
                           i.instance_id === item.instance_id
                       )
                   })

                   if (existing) {
                       alert('This vehicle has already been added to the invoice.')
                       return;
                   }
               }

                this.handleItemChanged(item)
            },
            handleItemChanged(item) {
                let index

                if (this.editingItemIndex === null) {
                    let newItem = new InvoiceItemSchema(item)
                    index = this.invoice.items.length
                    this.$set(this.invoice.items, this.invoice.items.length, newItem)
                } else {
                    index = this.editingItemIndex

                    for (let key in item) {
                        this.invoice.items[index][key] = item[key]
                    }
                }

                item.unit_net = this.getInvoiceHelper().itemNet(index)
                item.unit_vat = this.getInvoiceHelper().itemVat(index)
                item.unit_gross = this.getInvoiceHelper().itemTotal(index)
            },
            costMenuSelected(item, invoice_item, index) {
                switch (item.id) {
                    case 'delete':
                        this.invoice.items = this.invoice.items.filter(toFilter => {
                            return toFilter !== invoice_item
                        });
                    break;
                    case 'duplicate':
                        const duplicate = {...invoice_item}
                        item.duplicate = null
                        this.handleItemAdd(duplicate);
                    break;
                    case 'edit':
                        let editItem = Object.assign({}, invoice_item);

                        this.editingItemIndex = this.invoice.items.indexOf(invoice_item)

                        this.$refs['itemModal'].open(editItem)
                    break;
                }
            },
            getMenuOptions(item) {
                let items = [
                        {
                            id: 'edit',
                            icon: 'edit',
                            label: 'Edit',
                        },
                    ];

                if (!this.isActiveDeal && 
                    (
                        InvoiceItemTypes.TYPE_VEHICLE !== item.type || 
                        InvoiceItemTypes.TYPE_VEHICLE === item.type && !this.isDeal
                    ) && 
                    InvoiceItemTypes.TYPE_PART_EXCHANGE_SETTLEMENT !== item.type &&
                    InvoiceItemTypes.TYPE_WARRANTY !== item.type && 
                    InvoiceItemTypes.TYPE_EXEMPT_LINKED_WARRANTY !== item.type &&
                    InvoiceItemTypes.TYPE_GAP_RTI_INSURANCE !== item.type) {
                    items.push({
                        id: 'delete',
                        icon: 'delete',
                        label: 'Delete',
                    },
                    {
                        id: 'duplicate',
                        icon: 'content_copy',
                        label: 'Duplicate',
                    },)
                }

                return items;
            },
        },
    }
</script>